// Dedicated Fan tab — its own filtered list, own edit form (FanForm), and own
// profile experience (FanProfile). Reads from the shared Master CRM source
// (/api/crm/?types=Fan) but never reuses the performer-oriented CRMForm or
// CRMPreview so fan records stay free of collaboration/production language.
import React, { useEffect, useMemo, useState, useCallback } from "react";
import AppLayout from "../../components/AppLayout";
import { api } from "../../lib/api";
import { Plus, Search, Trash2, RefreshCw, Mail, ChevronLeft, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import FanForm from "./FanForm";
import FanProfile from "./FanProfile";
import { STATUSES, STATUS_HEX, STATUS_LABEL, getRecordStatuses, matchesStatusFilter } from "./constants";

const EMPTY_FAN = {
  stage_name: "", type: "Fan", status: "new_lead", statuses: ["new_lead"], source: "Organic",
  email: "", phone: "", twitter: "", instagram: "", onlyfans: "", justforfans: "",
  country: "", city: "", tags: [], kinks: "", boundaries: "", notes: "",
  photos: [], follow_ups: [],
};
const PAGE_SIZE = 25;

export default function FanCRM() {
  const [items, setItems] = useState([]);
  const [query, setQuery] = useState("");
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FAN);
  const [previewing, setPreviewing] = useState(null);
  const [statusFilter, setStatusFilter] = useState([]);
  const [statusFilterMode, setStatusFilterMode] = useState("any");
  const [platformFilter, setPlatformFilter] = useState("all");
  const [accountFilter, setAccountFilter] = useState("all");
  const [subscriptionFilter, setSubscriptionFilter] = useState("all");
  const [page, setPage] = useState(0);

  const load = useCallback(async () => {
    const [peopleResult, identityResult] = await Promise.all([
      api.get("/api/crm/?types=Fan&limit=1000"),
      api.get("/api/crm/fan-identities?limit=1000").catch(() => ({ data: [] })),
    ]);
    const byContact = new Map();
    (identityResult.data || []).forEach((identity) => {
      const current = byContact.get(identity.crm_id) || [];
      current.push(identity);
      byContact.set(identity.crm_id, current);
    });
    setItems((peopleResult.data || []).map((person) => ({
      ...person,
      source_identities: byContact.get(person.id) || [],
    })));
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const handler = (e) => { if (e.detail?.resourceType === "crm") setTimeout(load, 100); };
    window.addEventListener("gio:data-changed", handler);
    return () => window.removeEventListener("gio:data-changed", handler);
  }, [load]);

  const openNew = () => { setEditing("new"); setForm(EMPTY_FAN); };
  const openEdit = (p) => { setEditing(p.id); setForm({ ...EMPTY_FAN, ...p }); setPreviewing(null); };
  const closeForm = () => { setEditing(null); setForm(EMPTY_FAN); };
  const openPreview = (p) => setPreviewing(p);
  const closePreview = () => setPreviewing(null);

  const save = async () => {
    if (!form.stage_name.trim()) return toast.error("Fan name / handle required");
    try {
      const payload = { ...form, type: "Fan" };
      if (editing === "new") { await api.post("/api/crm/", payload); toast.success("Fan added"); }
      else { await api.put(`/api/crm/${editing}`, payload); toast.success("Saved"); }
      closeForm(); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this fan record?")) return;
    await api.delete(`/api/crm/${id}`);
    setPreviewing(null);
    load();
  };

  const filtered = useMemo(() => {
    const q = query.toLowerCase().trim();
    return items.filter((p) => matchesStatusFilter(p, statusFilter, statusFilterMode)
      && (platformFilter === "all" || (p.source_identities || []).some((identity) => identity.platform === platformFilter))
      && (accountFilter === "all" || (p.source_identities || []).some((identity) => `${identity.platform}:${identity.account_id}` === accountFilter))
      && (subscriptionFilter === "all" || (p.source_identities || []).some((identity) =>
        (identity.subscription_status || "unknown").toLowerCase() === subscriptionFilter))
      && (!q || `${p.stage_name} ${p.email} ${p.onlyfans} ${p.justforfans} ${(p.tags || []).join(" ")} ${(p.source_identities || []).map((identity) => `${identity.username || ""} ${identity.display_name || ""} ${identity.native_fan_id || ""}`).join(" ")}`.toLowerCase().includes(q)));
  }, [items, query, statusFilter, statusFilterMode, platformFilter, accountFilter, subscriptionFilter]);
  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const visible = filtered.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);
  const identities = items.flatMap((person) => person.source_identities || []);
  const platforms = [...new Set(identities.map((identity) => identity.platform))].sort();
  const accounts = [...new Map(identities.map((identity) => [`${identity.platform}:${identity.account_id}`, identity])).values()];
  const subscriptionStatuses = [...new Set(identities.map((identity) => (identity.subscription_status || "unknown").toLowerCase()))].sort();

  return (
    <AppLayout title="Fans" overline="CRM SUBVIEW · FAN + CUSTOMER OPERATIONS">
      <div className="flex items-center gap-3 mb-5 flex-wrap" data-testid="fans-toolbar">
        <div className="flex items-center gap-2 border bg-zinc-950 px-3 py-2 flex-1 min-w-[240px]" style={{ borderColor: "var(--gio-border)" }}>
          <Search size={14} className="text-zinc-600" />
          <input value={query} onChange={(e) => { setQuery(e.target.value); setPage(0); }} placeholder="Search fan, handle, email, segment..."
            data-testid="fans-search" className="bg-transparent text-white text-sm focus:outline-none flex-1" />
        </div>
        <FanStatusFilter value={statusFilter} onChange={(value) => { setStatusFilter(value); setPage(0); }} mode={statusFilterMode} onModeChange={setStatusFilterMode} />
        <select aria-label="Fan platform filter" value={platformFilter} onChange={(event) => { setPlatformFilter(event.target.value); setPage(0); }} className="gio-input w-auto">
          <option value="all">All platforms</option>{platforms.map((value) => <option key={value} value={value}>{value}</option>)}
        </select>
        <select aria-label="Creator account filter" value={accountFilter} onChange={(event) => { setAccountFilter(event.target.value); setPage(0); }} className="gio-input w-auto">
          <option value="all">All creator accounts</option>{accounts.map((identity) => <option key={`${identity.platform}:${identity.account_id}`} value={`${identity.platform}:${identity.account_id}`}>{identity.platform} · {identity.account_id}</option>)}
        </select>
        <select aria-label="Subscription status filter" value={subscriptionFilter} onChange={(event) => { setSubscriptionFilter(event.target.value); setPage(0); }} className="gio-input w-auto">
          <option value="all">All subscription states</option>{subscriptionStatuses.map((value) => <option key={value} value={value}>{value === "unknown" ? "Unknown" : value}</option>)}
        </select>
        <button onClick={load} data-testid="fans-refresh" className="btn-ghost inline-flex items-center gap-2"><RefreshCw size={14} /></button>
        <button onClick={openNew} data-testid="fans-add-button" className="btn-primary inline-flex items-center gap-2"><Plus size={14} /> Add fan</button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" data-testid="fans-grid">
        {filtered.length === 0 && (
          <div className="col-span-full text-center py-12" style={{ color: "var(--gio-muted-3)" }}>
            {items.length === 0 ? "No fans yet. Click 'Add fan' to create the first record." : "No matches."}
          </div>
        )}
        {visible.map((p) => (
          <div key={p.id} onClick={() => openPreview(p)} data-testid={`fan-card-${p.id}`}
            className="cursor-pointer rounded-xl border p-4 hover:-translate-y-0.5 transition"
            style={{ borderColor: "var(--gio-border)", background: "var(--gio-surface)" }}>
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="font-heading text-lg truncate" style={{ color: "var(--gio-text)" }}>{p.stage_name}</div>
                {p.email && <div className="text-[11px] truncate inline-flex items-center gap-1" style={{ color: "var(--gio-muted-2)" }}><Mail size={10} />{p.email}</div>}
              </div>
              <button onClick={(e) => { e.stopPropagation(); remove(p.id); }} data-testid={`fan-del-${p.id}`}
                className="text-zinc-600 hover:text-red-400 shrink-0"><Trash2 size={13} /></button>
            </div>
            <div className="flex flex-wrap gap-1.5 mt-3" data-testid={`fan-card-statuses-${p.id}`}>
              {getRecordStatuses(p).map((status) => {
                const color = STATUS_HEX[status] || "#94a3b8";
                return <span key={status} className="rounded-full border px-2 py-0.5 text-[9px] font-bold uppercase tracking-wide" style={{ color, borderColor: `${color}66`, background: `${color}18` }}>
                  {STATUS_LABEL[status] || status.replace(/_/g, " ")}
                </span>;
              })}
            </div>
            <div className="flex flex-wrap gap-1.5 mt-3">
              {p.onlyfans && <span className="text-[10px] px-1.5 py-0.5 rounded" style={{ background: "rgba(0,175,240,0.15)", color: "#00AFF0" }}>OF</span>}
              {p.justforfans && <span className="text-[10px] px-1.5 py-0.5 rounded" style={{ background: "rgba(245,158,11,0.15)", color: "#f59e0b" }}>JFF</span>}
              {(p.tags || []).slice(0, 3).map((t) => (
                <span key={t} className="text-[10px] px-1.5 py-0.5 rounded" style={{ background: "var(--gio-surface-hi)", color: "var(--gio-muted)" }}>{t}</span>
              ))}
            </div>
            {(p.source_identities || []).length > 0 && <div className="mt-3 space-y-1 border-t pt-2" style={{ borderColor: "var(--gio-border)" }}>
              {p.source_identities.map((identity) => <div key={`${identity.platform}:${identity.account_id}:${identity.native_fan_id}`} className="flex items-center justify-between gap-2 text-[10px]">
                <span className="truncate text-zinc-400">{identity.platform} · {identity.account_id} · @{identity.username || identity.display_name || "unknown"}</span>
                <span className="shrink-0 text-zinc-500">{identity.subscription_status || "Subscription unknown"}</span>
              </div>)}
              <p className="text-[10px] text-zinc-600">Last synced {p.source_identities.map((identity) => identity.last_observed_at).filter(Boolean).sort().at(-1) || "unknown"}</p>
            </div>}
          </div>
        ))}
      </div>
      {filtered.length > 0 && <div className="flex items-center justify-between border-t pt-3" style={{ borderColor: "var(--gio-border)" }}>
        <span className="text-xs" style={{ color: "var(--gio-muted-2)" }}>{filtered.length} fans · Page {page + 1} of {pageCount}</span>
        <div className="flex gap-2"><button type="button" aria-label="Previous fans page" onClick={() => setPage((current) => Math.max(0, current - 1))} disabled={page === 0} className="btn-ghost disabled:opacity-40"><ChevronLeft size={14} /></button><button type="button" aria-label="Next fans page" onClick={() => setPage((current) => Math.min(pageCount - 1, current + 1))} disabled={page + 1 >= pageCount} className="btn-ghost disabled:opacity-40"><ChevronRight size={14} /></button></div>
      </div>}

      {editing && <FanForm editing={editing} form={form} setForm={setForm} onClose={closeForm} onSave={save} />}
      {previewing && (
        <FanProfile
          person={previewing}
          onClose={closePreview}
          onEdit={() => openEdit(previewing)}
          onDelete={() => remove(previewing.id)}
          onLocalUpdate={(updated) => {
            const merged = { ...updated, source_identities: previewing.source_identities || [] };
            setPreviewing(merged); setItems((prev) => prev.map((it) => (it.id === updated.id ? merged : it)));
          }}
        />
      )}
    </AppLayout>
  );
}

function FanStatusFilter({ value, onChange, mode, onModeChange }) {
  const toggle = (status) => onChange(value.includes(status) ? value.filter((item) => item !== status) : [...value, status]);
  return (
    <details className="relative" data-testid="fans-status-filter">
      <summary className="list-none cursor-pointer select-none rounded-lg border bg-zinc-950 px-3 py-2 text-xs font-mono-ui uppercase tracking-wider text-white hover:border-[color:var(--gio-primary)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--gio-primary)]">
        Status{value.length ? ` · ${value.length}` : " · All"}
      </summary>
      <div className="absolute left-0 top-full z-40 mt-2 w-[min(90vw,34rem)] rounded-xl border p-3 shadow-xl" style={{ borderColor: "var(--gio-border)", background: "var(--gio-surface)", boxShadow: "var(--gio-shadow-lg)" }}>
        <div className="mb-3 flex items-center justify-between gap-3">
          <label className="flex items-center gap-2 text-xs" style={{ color: "var(--gio-muted)" }}>Match
            <select value={mode} onChange={(event) => onModeChange(event.target.value)} aria-label="Fan status filter matching mode" className="gio-input py-1.5 px-2 text-xs">
              <option value="any">Any selected</option><option value="all">All selected</option>
            </select>
          </label>
          <button type="button" onClick={() => onChange([])} disabled={!value.length} className="btn-ghost text-xs disabled:opacity-40">Clear</button>
        </div>
        <div className="grid max-h-72 grid-cols-2 gap-1.5 overflow-y-auto pr-1 sm:grid-cols-3">
          {STATUSES.map((status) => {
            const selected = value.includes(status);
            const color = STATUS_HEX[status] || "#94a3b8";
            return <button key={status} type="button" aria-pressed={selected} onClick={() => toggle(status)} className="rounded-lg border px-2 py-1.5 text-left text-[10px] font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--gio-primary)]" style={{ borderColor: selected ? `${color}99` : "var(--gio-border)", background: selected ? `${color}20` : "var(--gio-surface-2)", color: selected ? color : "var(--gio-muted)" }}>
              {STATUS_LABEL[status] || status.replace(/_/g, " ")}
            </button>;
          })}
        </div>
      </div>
    </details>
  );
}
